import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  consultationService,
  patientService,
  appointmentService,
  doctorService,
} from '@/services/api';
import { invoiceService } from '@/services/invoiceService';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { COMMON_MEDICINES as commonMedicines } from '@/constants';
import {
  getKolkataTodayString,
  kolkataToUtcIso,
  utcToKolkataParts,
  formatDate,
} from '@/utils/dateTime';
import { formatPhone, formatPatientAge, formatDoctorName } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Stethoscope,
  Activity,
  Heart,
  Thermometer,
  Wind,
  Plus,
  Trash2,
  Save,
  CheckCircle2,
  Calendar,
  User,
  AlertTriangle,
  FileText,
  Clock,
  Pill,
  Sparkles,
  Search,
  Copy,
  Printer,
  BookmarkPlus,
  Receipt,
  Lock,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  Check,
  Share2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PrescriptionTemplateModal } from '@/components/clinical/PrescriptionTemplateModal';
import { PrescriptionPrintModal } from '@/components/prescriptions/PrescriptionPrintModal';
import { InvoiceModal } from '@/components/billing/InvoiceModal';
import { cn } from '@/lib/utils';

const getTodayStr = () => getKolkataTodayString();
const getDateOffset = (offsetDays) => {
  const todayStr = getKolkataTodayString();
  const utcMs = new Date(kolkataToUtcIso(todayStr, '12:00:00')).getTime() + offsetDays * 86400000;
  const p = utcToKolkataParts(new Date(utcMs));
  return p ? p.dateString : todayStr;
};

const consultationSchema = z.object({
  chiefComplaints: z.string().optional(),
  symptoms: z.string().min(1, 'Please enter patient symptoms/complaints'),
  examination: z.string().optional(),
  vitals: z.object({
    systolic: z.coerce.number().min(50).max(260).optional().or(z.literal('')),
    diastolic: z.coerce.number().min(30).max(180).optional().or(z.literal('')),
    heartRate: z.coerce.number().min(30).max(220).optional().or(z.literal('')),
    temperature: z.coerce.number().min(94).max(108).optional().or(z.literal('')),
    respiratoryRate: z.coerce.number().min(6).max(60).optional().or(z.literal('')),
    oxygenSaturation: z.coerce.number().min(60).max(100).optional().or(z.literal('')),
    weight: z.coerce.number().min(1).max(350).optional().or(z.literal('')),
    height: z.coerce.number().min(30).max(250).optional().or(z.literal('')),
  }),
  diagnosis: z.string().min(1, 'Please specify primary diagnosis'),
  secondaryDiagnosis: z.string().optional(),
  prescription: z.array(
    z.object({
      medication: z.string().min(1, 'Medicine required'),
      dosage: z.string().optional(),
      amount: z.string().optional(),
      quantity: z.coerce.number().min(1, 'Quantity must be at least 1'),
      frequency: z.string().min(1, 'Frequency required'),
      duration: z.string().min(1, 'Duration required'),
      instructions: z.string().optional(),
    })
  ),
  notes: z.string().optional(),
  followUpRequired: z.boolean().default(false),
  followUpDate: z.string().optional(),
});

const commonFrequencies = [
  'Once daily (Morning)',
  'Once daily (Night)',
  'Twice daily (BD)',
  'Thrice daily (TDS)',
  'Four times daily (QID)',
  'As needed (SOS)',
];

const commonDurations = [
  '3 days',
  '5 days',
  '7 days',
  '10 days',
  '14 days',
  '30 days',
  'Ongoing',
];

const QUICK_COMPLAINTS = [
  'Fever',
  'Headache',
  'Cough',
  'Cold',
  'Body Ache',
  'Throat Pain',
  'Stomach Ache',
  'Fatigue',
  'Vomiting',
  'Breathlessness',
];

const COMMON_DIAGNOSES = [
  'Acute Upper Respiratory Tract Infection (URTI)',
  'Acute Viral Pharyngitis / Tonsillitis',
  'Acute Bronchitis',
  'Acute Gastroenteritis',
  'Essential Hypertension',
  'Type 2 Diabetes Mellitus',
  'Allergic Rhinitis',
  'Acid Peptic Disease (GERD)',
];

const SYSTEMS_LIST = [
  { id: 'general', name: 'General' },
  { id: 'ent', name: 'ENT' },
  { id: 'respiratory', name: 'Respiratory' },
  { id: 'cvs', name: 'CVS' },
  { id: 'abdomen', name: 'Abdomen' },
  { id: 'musculoskeletal', name: 'Musculoskeletal' },
  { id: 'skin', name: 'Skin' },
];

const ConsultationFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { activeClinicId, activeClinic, buildUrl, hospitalId } = useClinic();
  const { user } = useAuth();

  const queryPatientId = searchParams.get('patientId');
  const queryAppointmentId = searchParams.get('appointmentId');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consultationStatus, setConsultationStatus] = useState('IN_PROGRESS'); // DRAFT, IN_PROGRESS, COMPLETED
  const [patient, setPatient] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [pastConsultations, setPastConsultations] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(queryPatientId || '');
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  // Live financial status
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [patientPendingTotal, setPatientPendingTotal] = useState(0);

  // Modals & Navigation
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [currentConsultationData, setCurrentConsultationData] = useState(null);

  // New Case Sheet UI State (Reference: media_1790016664257.png)
  const [activeSection, setActiveSection] = useState('case-sheet');
  const [sharePrescription, setSharePrescription] = useState(true);
  const [selectedExamSystem, setSelectedExamSystem] = useState('general');
  const [structuredComplaints, setStructuredComplaints] = useState([
    { name: 'Fever', duration: '3 days', severity: 'Moderate' },
  ]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(consultationSchema),
    defaultValues: {
      chiefComplaints: 'Fever (3 days, Moderate)',
      symptoms: 'Fever (3 days, Moderate)',
      examination: 'General condition fair. Systemic examination: chest clear, throat mildly congested.',
      vitals: {
        systolic: 120,
        diastolic: 80,
        heartRate: 72,
        temperature: 98.4,
        respiratoryRate: 16,
        oxygenSaturation: 98,
        weight: 68,
        height: 170,
      },
      diagnosis: '',
      secondaryDiagnosis: '',
      prescription: [
        {
          medication: 'Paracetamol 650 mg (Dolo 650)',
          dosage: '1 tablet',
          amount: '1 tablet',
          quantity: 10,
          frequency: 'Twice daily (BD)',
          duration: '5 days',
          instructions: 'Take after meals with water',
        },
      ],
      notes: 'Hydrate well. Rest for 3 days. Review SOS if fever spikes above 101°F.',
      followUpRequired: true,
      followUpDate: getDateOffset(5),
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'prescription',
  });

  const watchWeight = watch('vitals.weight');
  const watchHeight = watch('vitals.height');
  const watchFollowUpRequired = watch('followUpRequired');

  // BMI Calculation
  const calculateBMI = () => {
    if (watchWeight && watchHeight && Number(watchHeight) > 0) {
      const heightInMeters = Number(watchHeight) / 100;
      const val = (Number(watchWeight) / (heightInMeters * heightInMeters)).toFixed(1);
      return val;
    }
    return null;
  };

  const bmi = calculateBMI();
  const getBmiBadge = (bmiValue) => {
    if (!bmiValue) return null;
    const num = Number(bmiValue);
    if (num < 18.5)
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
          Underweight
        </Badge>
      );
    if (num <= 24.9)
      return (
        <Badge
          variant="outline"
          className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
        >
          Normal
        </Badge>
      );
    if (num <= 29.9)
      return (
        <Badge
          variant="outline"
          className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
        >
          Overweight
        </Badge>
      );
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">
        Obese
      </Badge>
    );
  };

  const isCompleted = consultationStatus === 'COMPLETED';

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, queryPatientId, queryAppointmentId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const patientsRes = await patientService.getAll({ limit: 50 });
      setAllPatients(patientsRes.data || []);

      let targetPatientId = queryPatientId;
      let targetAppointment = null;

      if (queryAppointmentId) {
        try {
          targetAppointment = await appointmentService.getById(queryAppointmentId);
          setAppointment(targetAppointment);
          if (!targetPatientId && targetAppointment.patientId) {
            targetPatientId = targetAppointment.patientId;
          }
        } catch (e) {
          console.error(e);
        }
      }

      // If opening an existing consultation
      if (id && id !== 'new') {
        try {
          const consData = await consultationService.getById(id, activeClinicId, hospitalId);
          if (consData) {
            setCurrentConsultationData(consData);
            targetPatientId = consData.patientId || targetPatientId;
            setConsultationStatus(consData.status || 'COMPLETED');

            setValue('chiefComplaints', consData.chiefComplaints || consData.symptoms || '');
            setValue('symptoms', consData.symptoms || '');
            setValue('examination', consData.examination || '');
            setValue('diagnosis', consData.diagnosis || '');
            setValue('secondaryDiagnosis', consData.secondaryDiagnosis || '');
            setValue('notes', consData.doctorNotes || consData.notes || '');

            if (consData.vitals) {
              setValue(
                'vitals.systolic',
                consData.vitals.bloodPressure?.systolic || consData.bloodPressure?.systolic || 120
              );
              setValue(
                'vitals.diastolic',
                consData.vitals.bloodPressure?.diastolic || consData.bloodPressure?.diastolic || 80
              );
              setValue(
                'vitals.heartRate',
                consData.vitals.heartRate || consData.heartRate || 72
              );
              setValue(
                'vitals.temperature',
                consData.vitals.temperature || consData.temperature || 98.4
              );
              setValue(
                'vitals.respiratoryRate',
                consData.vitals.respiratoryRate || consData.respiratoryRate || 16
              );
              setValue(
                'vitals.oxygenSaturation',
                consData.vitals.oxygenSaturation || consData.oxygenSaturation || 98
              );
              setValue('vitals.weight', consData.vitals.weight || consData.weight || 68);
              setValue('vitals.height', consData.vitals.height || consData.height || 170);
            }

            const rawPresc = consData.prescriptions || consData.prescription || [];
            if (rawPresc.length > 0) {
              const mappedPresc = rawPresc.map((p) => ({
                medication: p.medicineName || p.medication || '',
                dosage: p.dosage || p.amount || '1 tablet',
                amount: p.amount || p.dosage || '1 tablet',
                quantity: Number(p.quantity) || 10,
                frequency: p.frequency || 'Twice daily (BD)',
                duration: p.duration || '5 days',
                instructions: p.instructions || p.notes || '',
              }));
              replace(mappedPresc);
            }

            setValue('followUpRequired', !!consData.followUpDate);
            setValue('followUpDate', consData.followUpDate || getDateOffset(7));

            // Load linked invoice
            const inv = await invoiceService.getByConsultationId(id, activeClinicId, hospitalId);
            if (inv) setLinkedInvoice(inv);
          }
        } catch (e) {
          console.error(e);
        }
      }

      if (!targetPatientId && patientsRes.data?.length > 0) {
        targetPatientId = patientsRes.data[0].id;
      }

      if (targetPatientId) {
        setSelectedPatientId(targetPatientId);
        const [patData, pastConsRes, patientInvoicesRes] = await Promise.all([
          patientService.getById(targetPatientId),
          consultationService.getAll({ patientId: targetPatientId }, hospitalId),
          invoiceService.getAll({ patientId: targetPatientId }, hospitalId).catch(() => ({ data: [] })),
        ]);
        setPatient(patData);
        const filteredHistory = (pastConsRes.data || []).filter((c) => c.id !== id);
        setPastConsultations(filteredHistory);

        // Compute total pending balance for patient
        const invList = patientInvoicesRes.data || [];
        const totalPending = invList.reduce(
          (sum, inv) => sum + (Number(inv.balanceAmount) || 0),
          0
        );
        setPatientPendingTotal(totalPending);
      }
    } catch (error) {
      toast.error('Failed to load consultation workspace');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientChange = async (newPatientId) => {
    setSelectedPatientId(newPatientId);
    try {
      const [patData, pastConsRes, patientInvoicesRes] = await Promise.all([
        patientService.getById(newPatientId),
        consultationService.getAll({ patientId: newPatientId }, hospitalId),
        invoiceService.getAll({ patientId: newPatientId }, hospitalId).catch(() => ({ data: [] })),
      ]);
      setPatient(patData);
      setPastConsultations((pastConsRes.data || []).filter((c) => c.id !== id));
      const invList = patientInvoicesRes.data || [];
      const totalPending = invList.reduce(
        (sum, inv) => sum + (Number(inv.balanceAmount) || 0),
        0
      );
      setPatientPendingTotal(totalPending);
    } catch (e) {
      toast.error('Failed to load patient history');
    }
  };

  // Structured Complaint Handlers
  const handleAddQuickComplaint = (tag) => {
    if (structuredComplaints.some((c) => c.name.toLowerCase() === tag.toLowerCase())) {
      return;
    }
    const updated = [...structuredComplaints, { name: tag, duration: '2-3 days', severity: 'Mild' }];
    setStructuredComplaints(updated);
    syncComplaintsToString(updated);
  };

  const handleAddCustomComplaint = () => {
    const updated = [
      ...structuredComplaints,
      { name: 'New Complaint', duration: '1 day', severity: 'Mild' },
    ];
    setStructuredComplaints(updated);
    syncComplaintsToString(updated);
  };

  const handleRemoveComplaint = (index) => {
    const updated = structuredComplaints.filter((_, i) => i !== index);
    setStructuredComplaints(updated);
    syncComplaintsToString(updated);
  };

  const syncComplaintsToString = (list) => {
    const text = list.map((c) => `${c.name} (${c.duration || 'N/A'}, ${c.severity || 'Mild'})`).join(', ');
    setValue('chiefComplaints', text);
    setValue('symptoms', text || 'Consultation follow-up');
  };

  const handleApplyTemplateMedicines = (templateItems) => {
    replace(templateItems);
  };

  const handleCopyPastPrescription = (pastPrescriptionItems) => {
    if (!pastPrescriptionItems || pastPrescriptionItems.length === 0) {
      toast.info('No medications in this past record');
      return;
    }
    const cloned = pastPrescriptionItems.map((p) => ({
      medication: p.medicineName || p.medication || '',
      dosage: p.dosage || p.amount || '1 tablet',
      amount: p.amount || p.dosage || '1 tablet',
      quantity: Number(p.quantity) || 10,
      frequency: p.frequency || 'Twice daily (BD)',
      duration: p.duration || '5 days',
      instructions: p.instructions || p.notes || '',
    }));
    replace(cloned);
    toast.success(`Copied ${cloned.length} medications to active prescription`);
  };

  const handleCopyPastVitals = (pastVitals) => {
    if (!pastVitals) return;
    if (pastVitals.bloodPressure?.systolic)
      setValue('vitals.systolic', pastVitals.bloodPressure.systolic);
    if (pastVitals.bloodPressure?.diastolic)
      setValue('vitals.diastolic', pastVitals.bloodPressure.diastolic);
    if (pastVitals.heartRate) setValue('vitals.heartRate', pastVitals.heartRate);
    if (pastVitals.temperature) setValue('vitals.temperature', pastVitals.temperature);
    if (pastVitals.oxygenSaturation)
      setValue('vitals.oxygenSaturation', pastVitals.oxygenSaturation);
    if (pastVitals.weight) setValue('vitals.weight', pastVitals.weight);
    if (pastVitals.height) setValue('vitals.height', pastVitals.height);
    toast.success('Applied historical vitals as current reference');
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const buildPayload = (formData, isDraft) => {
    return {
      patientId: patient?.id || selectedPatientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : '',
      patientCode: patient?.patientCode || '',
      doctorId: appointment?.doctorId || user?.id || 'doc-001',
      doctorName: formatDoctorName(user || appointment?.doctor || 'Dr. Consultant'),
      clinicId: appointment?.clinicId || activeClinicId,
      appointmentId: appointment?.id || queryAppointmentId || null,
      date: getTodayStr(),
      status: isDraft ? 'DRAFT' : 'COMPLETED',
      isDraft,
      chiefComplaints: formData.chiefComplaints || formData.symptoms,
      symptoms: formData.symptoms,
      examination: formData.examination,
      vitals: {
        bloodPressure: {
          systolic: Number(formData.vitals.systolic) || 120,
          diastolic: Number(formData.vitals.diastolic) || 80,
        },
        heartRate: Number(formData.vitals.heartRate) || 72,
        temperature: Number(formData.vitals.temperature) || 98.4,
        respiratoryRate: Number(formData.vitals.respiratoryRate) || 16,
        oxygenSaturation: Number(formData.vitals.oxygenSaturation) || 98,
        weight: Number(formData.vitals.weight) || 68,
        height: Number(formData.vitals.height) || 170,
      },
      diagnosis: formData.diagnosis,
      secondaryDiagnosis: formData.secondaryDiagnosis,
      prescriptions: (formData.prescription || []).map((p, idx) => ({
        id: `pi-${Date.now()}-${idx}`,
        medicineName: p.medication,
        dosage: p.dosage || p.amount || '1 tablet',
        amount: p.amount || p.dosage || '1 tablet',
        quantity: Number(p.quantity) || 10,
        frequency: p.frequency,
        duration: p.duration,
        instructions: p.instructions || '',
        notes: p.instructions || '',
        displayOrder: idx + 1,
      })),
      doctorNotes: formData.notes || '',
      notes: formData.notes || '',
      followUpRequired: formData.followUpRequired,
      followUpDate: formData.followUpRequired ? formData.followUpDate : null,
      shareWithPatient: sharePrescription,
    };
  };

  const onSave = async (formData, isDraft = false) => {
    if (!patient) {
      toast.error('Please select a patient');
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload(formData, isDraft);

      let saved;
      if (id && id !== 'new') {
        if (isDraft) {
          saved = await consultationService.saveDraft(id, payload, hospitalId);
        } else {
          saved = await consultationService.complete(id, payload, hospitalId);
        }
      } else {
        if (isDraft) {
          saved = await consultationService.saveDraft(null, payload, hospitalId);
        } else {
          saved = await consultationService.complete(null, payload, hospitalId);
        }
      }

      setCurrentConsultationData(saved);

      if (isDraft) {
        setConsultationStatus('DRAFT');
        toast.success('Consultation draft saved. You can resume anytime.');
      } else {
        setConsultationStatus('COMPLETED');
        toast.success('Consultation finalized and locked!');
        if (sharePrescription) {
          toast.info('Prescription sharing flag recorded for patient delivery.');
        }
        // Automatically offer Invoice modal after completion
        setInvoiceModalOpen(true);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save clinical encounter');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="consultation-form-loading">
        <Skeleton className="h-16 w-full rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 md:col-span-2" />
        </div>
      </div>
    );
  }

  const ageDisplay = patient ? formatPatientAge(patient.age, patient.dateOfBirth) : '';
  const initials = patient
    ? `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase()
    : 'P';

  const pendingAmountToShow =
    linkedInvoice?.balanceAmount ?? (patientPendingTotal > 0 ? patientPendingTotal : 0);

  return (
    <div className="space-y-4 max-w-7xl mx-auto pb-28" data-testid="consultation-form-page">
      {/* 1. SLIM PATIENT HEADER STRIP (Reference: media_1790016664257.png) */}
      <div className="bg-slate-900 text-white rounded-xl px-4 py-3 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        {/* Left: Avatar + Details */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(buildUrl('consultations'))}
            className="text-slate-400 hover:text-white rounded-full h-8 w-8 -ml-1 shrink-0"
            data-testid="back-to-queue-btn"
            title="Back to queue"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <Avatar className="h-10 w-10 rounded-lg ring-1 ring-teal-500/40 bg-teal-800 text-teal-100 font-bold shrink-0">
            <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
          </Avatar>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm sm:text-base text-white">
                {patient ? `${patient.firstName} ${patient.lastName}` : 'No Patient Selected'}
              </span>

              {/* Financial Pending Badge (Clicking opens Invoice Modal) */}
              {pendingAmountToShow > 0 ? (
                <button
                  type="button"
                  onClick={() => setInvoiceModalOpen(true)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 transition-colors cursor-pointer"
                  title="Click to view/bill invoice"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  Pending • ₹{pendingAmountToShow.toLocaleString('en-IN')} ↗
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <Check className="h-3 w-3" /> Settled
                </span>
              )}
            </div>

            <p className="text-[11px] sm:text-xs text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
              <span>
                {patient?.gender || 'Gender'}, {ageDisplay || 'Age'}
              </span>
              <span className="text-slate-600">•</span>
              <span className="font-mono text-slate-300">{patient?.phone ? formatPhone(patient.phone) : 'No Phone'}</span>
              <span className="text-slate-600">•</span>
              <span className="font-mono text-slate-300">
                {patient?.patientCode || (patient ? `#${patient.id.slice(-6)}` : '')}
              </span>
            </p>
          </div>
        </div>

        {/* Right: Specialty, Doctor, Status */}
        <div className="flex items-center gap-3 self-end md:self-center text-xs">
          <div className="text-right hidden sm:block">
            <p className="text-slate-400 text-[11px]">
              Speciality: <strong className="text-slate-200">{activeClinic?.name || 'General OPD'}</strong>
            </p>
            <p className="text-slate-400 text-[11px]">
              Attending: <strong className="text-slate-200">{formatDoctorName(user || appointment?.doctor || 'Dr. Consultant')}</strong>
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isCompleted ? (
              <Badge className="bg-emerald-600 text-white text-[11px] font-bold flex items-center gap-1">
                <Lock className="h-3 w-3" /> Finalized
              </Badge>
            ) : consultationStatus === 'DRAFT' ? (
              <Badge className="bg-amber-500 text-white text-[11px] font-bold">
                Draft Saved
              </Badge>
            ) : (
              <Badge className="bg-teal-600 text-white text-[11px] font-medium">
                Active Encounter
              </Badge>
            )}

            {patient && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => navigate(buildUrl(`patients/${patient.id}`))}
                className="h-7 text-[11px] bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              >
                EHR Profile
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 2. TOP SECTION NAVIGATION BAR (Reference: media_1790016664257.png) */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs px-2 flex items-center justify-between overflow-x-auto">
        <div className="flex items-center gap-1 h-11">
          {[
            { id: 'section-complaints', label: 'Case Sheet', icon: Stethoscope },
            { id: 'section-exam', label: 'Visual Examination', icon: Eye },
            { id: 'section-vitals', label: 'Vitals & Biometrics', icon: Activity },
            { id: 'section-diagnosis', label: 'Clinical Diagnosis', icon: FileText },
            { id: 'section-prescription', label: 'Prescriptions', icon: Pill },
            { id: 'section-notes', label: 'Doctor Notes & Advice', icon: Clock },
            { id: 'section-past-visits', label: `Past Visits (${pastConsultations.length})`, icon: RotateCcw },
          ].map((sec) => {
            const Icon = sec.icon;
            const isActive = activeSection === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => scrollToSection(sec.id)}
                className={cn(
                  'h-11 px-3.5 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap',
                  isActive
                    ? 'border-teal-600 text-teal-700 bg-teal-50/40'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                )}
              >
                <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-teal-600' : 'text-slate-400')} />
                {sec.label}
              </button>
            );
          })}
        </div>

        {/* Patient Switcher if not bound to an appointment */}
        {!queryAppointmentId && allPatients.length > 0 && !isCompleted && (
          <div className="hidden lg:flex items-center gap-2 pr-2">
            <span className="text-[11px] text-slate-400 whitespace-nowrap">Switch Patient:</span>
            <Select value={selectedPatientId} onValueChange={handlePatientChange}>
              <SelectTrigger className="h-7 text-xs w-48 border-slate-200">
                <SelectValue placeholder="Select patient" />
              </SelectTrigger>
              <SelectContent>
                {allPatients.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    {p.firstName} {p.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* 3. MAIN WORKSPACE WITH LEFT VERTICAL MINI-DOCK (Reference: media_1790016664257.png) */}
      <div className="flex items-start gap-4">
        {/* Left Sticky Mini-Dock Icon Rail */}
        <div className="hidden md:flex flex-col items-center gap-2 p-2 bg-white rounded-xl border border-slate-200 shadow-xs sticky top-4 shrink-0">
          {[
            { id: 'section-complaints', title: 'Case Sheet / Complaints', icon: Stethoscope },
            { id: 'section-exam', title: 'Physical Examination', icon: Eye },
            { id: 'section-vitals', title: 'Vitals & Biometrics', icon: Activity },
            { id: 'section-diagnosis', title: 'Diagnosis', icon: FileText },
            { id: 'section-prescription', title: 'Prescription Builder', icon: Pill },
            { id: 'section-notes', title: 'Notes & Follow-up', icon: Clock },
            { id: 'section-past-visits', title: 'Past Consultation History', icon: RotateCcw },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                title={item.title}
                className={cn(
                  'w-9 h-9 rounded-lg flex items-center justify-center transition-all',
                  isActive
                    ? 'bg-teal-600 text-white shadow-xs'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
        </div>

        {/* Clinical Form Content */}
        <form className="flex-1 space-y-6">
          {/* LOCK BANNER IF COMPLETED */}
          {isCompleted && (
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-emerald-800">
                <Lock className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>
                  This clinical encounter has been finalized and locked for medical audit compliance.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPrintModalOpen(true)}
                  className="text-xs border-emerald-300 text-emerald-800 bg-white"
                >
                  <Printer className="h-3.5 w-3.5 mr-1" /> Print Prescription
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setInvoiceModalOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                >
                  <Receipt className="h-3.5 w-3.5 mr-1" /> View / Bill Invoice
                </Button>
              </div>
            </div>
          )}

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 1: CHIEF COMPLAINTS (Reference: media_1790016664257.png)   */}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-complaints" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-teal-600" />
                  Chief Complaints
                </CardTitle>
                <CardDescription className="text-xs">
                  Click quick symptom tags or add structured complaints with duration & severity.
                </CardDescription>
              </div>
              {!isCompleted && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddCustomComplaint}
                  className="text-xs text-teal-700 border-teal-300 hover:bg-teal-50 h-8"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  + Add Chief Complaint
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Symptom Chips */}
              {!isCompleted && (
                <div className="flex flex-wrap gap-1.5 pb-2 border-b border-slate-100">
                  <span className="text-[11px] text-slate-400 self-center mr-1">Quick Add:</span>
                  {QUICK_COMPLAINTS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleAddQuickComplaint(tag)}
                      className="px-2.5 py-1 text-xs rounded-full bg-slate-100 hover:bg-teal-100 hover:text-teal-800 text-slate-700 border border-slate-200 transition-colors"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              )}

              {/* Structured Complaints Cards */}
              <div className="space-y-2">
                {structuredComplaints.map((comp, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 rounded-lg border border-slate-200 bg-slate-50/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        disabled={isCompleted}
                        value={comp.name}
                        onChange={(e) => {
                          const updated = [...structuredComplaints];
                          updated[idx].name = e.target.value;
                          setStructuredComplaints(updated);
                          syncComplaintsToString(updated);
                        }}
                        placeholder="Complaint description..."
                        className="h-8 text-xs bg-white flex-1"
                      />

                      <Input
                        disabled={isCompleted}
                        value={comp.duration}
                        onChange={(e) => {
                          const updated = [...structuredComplaints];
                          updated[idx].duration = e.target.value;
                          setStructuredComplaints(updated);
                          syncComplaintsToString(updated);
                        }}
                        placeholder="e.g. 3 days"
                        className="h-8 text-xs bg-white w-28"
                      />

                      <Select
                        disabled={isCompleted}
                        value={comp.severity}
                        onValueChange={(val) => {
                          const updated = [...structuredComplaints];
                          updated[idx].severity = val;
                          setStructuredComplaints(updated);
                          syncComplaintsToString(updated);
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs w-28 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Mild" className="text-xs">Mild</SelectItem>
                          <SelectItem value="Moderate" className="text-xs">Moderate</SelectItem>
                          <SelectItem value="Severe" className="text-xs">Severe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {!isCompleted && structuredComplaints.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveComplaint(idx)}
                        className="h-8 w-8 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Free text symptoms sync */}
              <div className="space-y-1">
                <Label htmlFor="symptoms" className="text-xs text-slate-600 font-semibold">
                  Consolidated Complaints Summary <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="symptoms"
                  disabled={isCompleted}
                  {...register('symptoms')}
                  rows={2}
                  className="text-xs"
                  placeholder="Summary of patient presentation..."
                />
                {errors.symptoms && (
                  <p className="text-[11px] text-red-500">{errors.symptoms.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 2: ON EXAMINATION (Reference: media_1790016664257.png)      */}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-exam" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Eye className="h-4 w-4 text-teal-600" />
                On Examination / Clinical Findings
              </CardTitle>
              <CardDescription className="text-xs">
                Generic OPD systemic clinical examination findings and observations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* System selector buttons */}
              <div className="flex flex-wrap gap-1.5 pb-2 border-b border-slate-100">
                {SYSTEMS_LIST.map((sys) => (
                  <button
                    key={sys.id}
                    type="button"
                    onClick={() => setSelectedExamSystem(sys.id)}
                    className={cn(
                      'px-3 py-1 text-xs rounded-lg font-medium transition-colors',
                      selectedExamSystem === sys.id
                        ? 'bg-teal-600 text-white shadow-xs'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    )}
                  >
                    {sys.name}
                  </button>
                ))}
              </div>

              {/* Examination Notes */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="examination" className="text-xs text-slate-700 font-semibold">
                    Physical Examination Findings ({selectedExamSystem.toUpperCase()})
                  </Label>
                  {!isCompleted && (
                    <div className="flex items-center gap-1.5">
                      {['NAD / Normal', 'Erythema noted', 'Tenderness (+)', 'Clear sounds'].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => {
                            const cur = getValues('examination') || '';
                            setValue(
                              'examination',
                              cur ? `${cur}; [${selectedExamSystem.toUpperCase()}] ${preset}` : `[${selectedExamSystem.toUpperCase()}] ${preset}`
                            );
                          }}
                          className="px-2 py-0.5 text-[10px] rounded bg-slate-100 hover:bg-slate-200 text-slate-600"
                        >
                          + {preset}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Textarea
                  id="examination"
                  disabled={isCompleted}
                  {...register('examination')}
                  placeholder="Document physical findings e.g. Bilateral tonsillar enlargement with mild exudates. Chest clear..."
                  rows={3}
                  className="text-xs"
                />
              </div>
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 3: VITALS & BIOMETRICS (Reference: media_1790016664257.png) */}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-vitals" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-teal-600" />
                  Vitals & Biometrics
                </CardTitle>
                <CardDescription className="text-xs">
                  Physiological baseline measurements
                </CardDescription>
              </div>
              {bmi && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    BMI: <strong className="font-mono text-slate-900">{bmi}</strong>
                  </span>
                  {getBmiBadge(bmi)}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">BP (Systolic / Diastolic)</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      disabled={isCompleted}
                      {...register('vitals.systolic')}
                      placeholder="120"
                      className="text-xs text-center font-mono"
                    />
                    <span className="text-slate-400">/</span>
                    <Input
                      disabled={isCompleted}
                      {...register('vitals.diastolic')}
                      placeholder="80"
                      className="text-xs text-center font-mono"
                    />
                  </div>
                  <span className="text-[10px] text-slate-400">mmHg</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Heart Rate</Label>
                  <div className="relative">
                    <Input
                      disabled={isCompleted}
                      {...register('vitals.heartRate')}
                      placeholder="72"
                      className="text-xs font-mono"
                    />
                    <Heart className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-rose-500" />
                  </div>
                  <span className="text-[10px] text-slate-400">bpm</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Temperature</Label>
                  <div className="relative">
                    <Input
                      disabled={isCompleted}
                      {...register('vitals.temperature')}
                      placeholder="98.4"
                      className="text-xs font-mono"
                    />
                    <Thermometer className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-500" />
                  </div>
                  <span className="text-[10px] text-slate-400">°F</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Oxygen (SpO2)</Label>
                  <div className="relative">
                    <Input
                      disabled={isCompleted}
                      {...register('vitals.oxygenSaturation')}
                      placeholder="98"
                      className="text-xs font-mono"
                    />
                    <Wind className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-blue-500" />
                  </div>
                  <span className="text-[10px] text-slate-400">% SpO2</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Respiratory Rate</Label>
                  <Input
                    disabled={isCompleted}
                    {...register('vitals.respiratoryRate')}
                    placeholder="16"
                    className="text-xs font-mono"
                  />
                  <span className="text-[10px] text-slate-400">breaths/min</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Weight (kg)</Label>
                  <Input
                    disabled={isCompleted}
                    {...register('vitals.weight')}
                    placeholder="68"
                    className="text-xs font-mono"
                  />
                  <span className="text-[10px] text-slate-400">kg</span>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Height (cm)</Label>
                  <Input
                    disabled={isCompleted}
                    {...register('vitals.height')}
                    placeholder="170"
                    className="text-xs font-mono"
                  />
                  <span className="text-[10px] text-slate-400">cm</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 4: DIAGNOSIS (Reference: media_1790016664257.png)          */}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-diagnosis" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-teal-600" />
                Clinical Diagnosis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quick Diagnosis tags */}
              {!isCompleted && (
                <div className="flex flex-wrap gap-1.5 pb-2 border-b border-slate-100">
                  <span className="text-[11px] text-slate-400 self-center mr-1">Suggested:</span>
                  {COMMON_DIAGNOSES.map((diag) => (
                    <button
                      key={diag}
                      type="button"
                      onClick={() => setValue('diagnosis', diag)}
                      className="px-2.5 py-1 text-xs rounded-full bg-slate-100 hover:bg-teal-100 hover:text-teal-800 text-slate-700 border border-slate-200 transition-colors"
                    >
                      {diag}
                    </button>
                  ))}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="diagnosis" className="text-xs font-semibold text-slate-700">
                  Primary Diagnosis <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="diagnosis"
                  disabled={isCompleted}
                  {...register('diagnosis')}
                  placeholder="Enter or select primary clinical diagnosis..."
                  className="text-xs font-medium"
                />
                {errors.diagnosis && (
                  <p className="text-[11px] text-red-500">{errors.diagnosis.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="secondaryDiagnosis" className="text-xs font-semibold text-slate-700">
                  Differential / Secondary Diagnosis (Optional)
                </Label>
                <Input
                  id="secondaryDiagnosis"
                  disabled={isCompleted}
                  {...register('secondaryDiagnosis')}
                  placeholder="e.g. Allergic Rhinitis, GERD"
                  className="text-xs"
                />
              </div>
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 5: PRESCRIPTION (Reference: media_1790016664257.png)        */}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-prescription" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Pill className="h-4 w-4 text-teal-600" />
                  Prescriptions ({fields.length})
                </CardTitle>
                <CardDescription className="text-xs">
                  Structured drug rows with dosage, quantity, frequency, and instructions
                </CardDescription>
              </div>
              {!isCompleted && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setTemplateModalOpen(true)}
                    className="text-xs border-teal-300 text-teal-700 hover:bg-teal-50 h-8"
                  >
                    <BookmarkPlus className="h-3.5 w-3.5 mr-1" />
                    Apply Template ▼
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      append({
                        medication: '',
                        dosage: '1 tablet',
                        amount: '1 tablet',
                        quantity: 10,
                        frequency: 'Twice daily (BD)',
                        duration: '5 days',
                        instructions: 'After meals',
                      })
                    }
                    className="text-xs border-slate-300 text-slate-700 h-8"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Medication
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.map((item, index) => (
                <div
                  key={item.id}
                  className="p-3.5 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                    <div className="sm:col-span-5 space-y-1">
                      <Label className="text-[11px] text-slate-600 font-semibold">
                        Medicine #{index + 1} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        disabled={isCompleted}
                        {...register(`prescription.${index}.medication`)}
                        placeholder="Search medicine (e.g. Dolo 650, Pan 40)..."
                        className="text-xs bg-white"
                        list="medicine-suggestions"
                      />
                    </div>

                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-[11px] text-slate-600">Dosage</Label>
                      <Input
                        disabled={isCompleted}
                        {...register(`prescription.${index}.amount`)}
                        placeholder="1 tab / 5 ml"
                        className="text-xs bg-white"
                      />
                    </div>

                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-[11px] text-slate-600">Quantity</Label>
                      <Input
                        disabled={isCompleted}
                        type="number"
                        min="1"
                        {...register(`prescription.${index}.quantity`)}
                        placeholder="10"
                        className="text-xs bg-white"
                      />
                    </div>

                    <div className="sm:col-span-3 space-y-1">
                      <Label className="text-[11px] text-slate-600">Frequency</Label>
                      <Input
                        disabled={isCompleted}
                        {...register(`prescription.${index}.frequency`)}
                        placeholder="Twice daily (BD)"
                        className="text-xs bg-white"
                        list="frequency-suggestions"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                    <div className="sm:col-span-3 space-y-1">
                      <Label className="text-[11px] text-slate-600">Duration</Label>
                      <Input
                        disabled={isCompleted}
                        {...register(`prescription.${index}.duration`)}
                        placeholder="5 days"
                        className="text-xs bg-white"
                        list="duration-suggestions"
                      />
                    </div>

                    <div className="sm:col-span-8 space-y-1">
                      <Label className="text-[11px] text-slate-600">Instructions / Food Timing</Label>
                      <Input
                        disabled={isCompleted}
                        {...register(`prescription.${index}.instructions`)}
                        placeholder="e.g. Take after food with lukewarm water"
                        className="text-xs bg-white"
                      />
                    </div>

                    <div className="sm:col-span-1 flex justify-end pt-5">
                      {!isCompleted && fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                          className="h-8 w-8 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              <datalist id="medicine-suggestions">
                {commonMedicines.map((med, idx) => (
                  <option key={idx} value={med} />
                ))}
              </datalist>
              <datalist id="frequency-suggestions">
                {commonFrequencies.map((freq, idx) => (
                  <option key={idx} value={freq} />
                ))}
              </datalist>
              <datalist id="duration-suggestions">
                {commonDurations.map((dur, idx) => (
                  <option key={idx} value={dur} />
                ))}
              </datalist>
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 6: DOCTOR NOTES & FOLLOW-UP (Reference: media_1790016664257)*/}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-notes" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Clock className="h-4 w-4 text-teal-600" />
                Doctor Notes & Follow-up Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="notes" className="text-xs text-slate-700 font-semibold">
                  Clinical Advice & Patient Instructions
                </Label>
                <Textarea
                  id="notes"
                  disabled={isCompleted}
                  {...register('notes')}
                  placeholder="e.g. Hydrate well (3L/day). Strict salt restriction. Review SOS if high fever persists > 48h."
                  rows={2}
                  className="text-xs"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Switch
                    disabled={isCompleted}
                    checked={watchFollowUpRequired}
                    onCheckedChange={(checked) => setValue('followUpRequired', checked)}
                    id="follow-up-switch"
                  />
                  <Label htmlFor="follow-up-switch" className="text-xs font-semibold text-slate-800 cursor-pointer">
                    Schedule Follow-up Visit
                  </Label>
                </div>

                {watchFollowUpRequired && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 whitespace-nowrap">Follow-up Date:</Label>
                    <Input
                      disabled={isCompleted}
                      type="date"
                      {...register('followUpDate')}
                      min={getTodayStr()}
                      className="text-xs w-44"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 7: PAST VISITS ACCORDION                                    */}
          {/* ------------------------------------------------------------------ */}
          <Card id="section-past-visits" className="border-slate-200 scroll-mt-20">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-teal-600" />
                  Past Visits ({pastConsultations.length})
                </CardTitle>
                <CardDescription className="text-xs">
                  Review historical encounters and copy past prescriptions or vitals
                </CardDescription>
              </div>
              {patient && (
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  onClick={() => navigate(buildUrl(`patients/${patient.id}`))}
                  className="text-teal-600 text-xs p-0 h-auto"
                >
                  View Patient Profile →
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {pastConsultations.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-4 text-center">
                  No previous consultation encounters recorded for this patient.
                </p>
              ) : (
                pastConsultations.map((cons) => {
                  const isExpanded = expandedHistoryId === cons.id;
                  const prescList = cons.prescriptions || cons.prescription || [];

                  return (
                    <div
                      key={cons.id}
                      className="border border-slate-200 rounded-lg p-3 bg-white space-y-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-slate-900 block">{formatDate(cons.date, 'FULL')}</span>
                          <span className="text-[11px] text-slate-500">{formatDoctorName(cons.doctor)}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedHistoryId(isExpanded ? null : cons.id)}
                          className="h-7 px-2 text-slate-500"
                        >
                          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </Button>
                      </div>

                      <div className="bg-slate-50 p-2 rounded text-[11px]">
                        <p className="text-slate-700">
                          <strong>Diagnosis:</strong> {cons.diagnosis || 'None specified'}
                        </p>
                      </div>

                      {isExpanded && (
                        <div className="pt-2 border-t border-slate-100 space-y-2">
                          {cons.vitals && (
                            <div className="text-[11px] text-slate-600">
                              <p className="font-semibold text-slate-700">Vitals recorded:</p>
                              <p>
                                BP: {cons.vitals.bloodPressure?.systolic}/{cons.vitals.bloodPressure?.diastolic} mmHg • HR: {cons.vitals.heartRate} bpm • Wt: {cons.vitals.weight} kg
                              </p>
                              {!isCompleted && (
                                <Button
                                  type="button"
                                  variant="link"
                                  size="sm"
                                  onClick={() => handleCopyPastVitals(cons.vitals)}
                                  className="text-teal-600 text-[11px] p-0 h-auto mt-1"
                                >
                                  Use previous vitals
                                </Button>
                              )}
                            </div>
                          )}

                          {prescList.length > 0 && (
                            <div className="text-[11px] text-slate-600 space-y-1">
                              <p className="font-semibold text-slate-700">Prescribed ({prescList.length} medicines):</p>
                              {prescList.map((p, idx) => (
                                <p key={idx} className="truncate">
                                  • {p.medicineName || p.medication} ({p.amount || p.dosage} - {p.frequency})
                                </p>
                              ))}
                              {!isCompleted && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCopyPastPrescription(prescList)}
                                  className="w-full text-xs border-teal-300 text-teal-700 hover:bg-teal-50 h-7 mt-1"
                                >
                                  <Copy className="h-3 w-3 mr-1" /> Copy Previous Prescription
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </form>
      </div>

      {/* 4. FIXED STICKY BOTTOM ACTION BAR (Reference: media_1790016664257.png) */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-lg px-4 sm:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Left: Abstract Share Checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="share-rx-check"
            checked={sharePrescription}
            onChange={(e) => setSharePrescription(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
          />
          <Label
            htmlFor="share-rx-check"
            className="text-xs text-slate-700 cursor-pointer flex items-center gap-1.5 select-none"
          >
            <Share2 className="h-3.5 w-3.5 text-teal-600" />
            Share prescription with patient (SMS / WhatsApp)
          </Label>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPrintModalOpen(true)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-semibold h-9"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Preview / Print Rx
          </Button>

          {!isCompleted ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={handleSubmit((data) => onSave(data, true))}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-semibold h-9"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>

              <Button
                type="button"
                disabled={saving}
                onClick={handleSubmit((data) => onSave(data, false))}
                className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-sm h-9 px-4"
                data-testid="complete-consultation-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                {saving ? 'Finalizing...' : 'End Consultation'}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={() => setInvoiceModalOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-sm h-9 px-4"
            >
              <Receipt className="h-4 w-4 mr-1.5" />
              Generate / View Invoice
            </Button>
          )}
        </div>
      </div>

      {/* MODALS */}
      {/* 1. Prescription Template Modal */}
      <PrescriptionTemplateModal
        open={templateModalOpen}
        onOpenChange={setTemplateModalOpen}
        onApplyTemplate={handleApplyTemplateMedicines}
        initialMedicines={getValues('prescription') || []}
        clinicId={activeClinicId}
        doctorId={appointment?.doctorId || user?.id}
      />

      {/* 2. Prescription Print Modal */}
      <PrescriptionPrintModal
        open={printModalOpen}
        onOpenChange={setPrintModalOpen}
        consultation={currentConsultationData || {
          date: getTodayStr(),
          chiefComplaints: getValues('chiefComplaints') || getValues('symptoms'),
          diagnosis: getValues('diagnosis'),
          prescriptions: getValues('prescription'),
          vitals: getValues('vitals'),
          doctorNotes: getValues('notes'),
          followUpDate: getValues('followUpRequired') ? getValues('followUpDate') : null,
        }}
        patient={patient}
        doctor={user}
        clinicName={activeClinic?.name}
      />

      {/* 3. Invoice & Payment Modal */}
      <InvoiceModal
        open={invoiceModalOpen}
        onOpenChange={setInvoiceModalOpen}
        consultation={currentConsultationData}
        patient={patient}
        doctor={user}
        clinicId={activeClinicId}
        hospitalId={hospitalId}
        onInvoiceCreated={(newInv) => {
          setLinkedInvoice(newInv);
        }}
      />
    </div>
  );
};

export default ConsultationFormPage;
